/* Bundle stories: Instagram-style viewer. Vanilla custom element. */
class BundleStories extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.duration = 5000;
    this.index = 0;
    this.elapsed = 0;
    this.holding = false;
    this.raf = 0;
    this.initRow();
    this.key = this.dataset.storageKey || 'jm-bundle-stories-viewed';
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.avatars = [...this.querySelectorAll('[data-story-open]')];
    this.dialog = this.querySelector('[data-viewer]');
    this.stage = this.querySelector('[data-stage]');
    this.panels = [...this.querySelectorAll('[data-story]')];
    this.progress = this.querySelector('[data-progress]');
    if (!this.dialog || !this.panels.length) return;

    this.progress.innerHTML = this.panels.map(() => '<span class="bs-seg"><i></i></span>').join('');
    this.segs = [...this.progress.children];

    this.viewed = this.readViewed();
    this.avatars.forEach((a) => {
      if (this.viewed.includes(a.dataset.storyId)) a.setAttribute('data-seen', '');
      a.addEventListener('click', () => { this.opener = a; this.open(Number(a.dataset.storyOpen)); });
    });

    this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-prev]').addEventListener('click', () => this.prev());
    this.querySelector('[data-next]').addEventListener('click', () => this.next());
    this.dialog.addEventListener('close', () => this.onClose());
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.next();
      if (e.key === 'ArrowLeft') this.prev();
    });
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    document.addEventListener('visibilitychange', () => { this.tabHidden = document.hidden; });

    // Tap / hold / swipe
    let down = null;
    this.stage.addEventListener('pointerdown', (e) => {
      if (e.target.closest('a,button,form')) return;
      down = { x: e.clientX, y: e.clientY, t: performance.now() };
      this.holding = true;
    });
    const end = (e) => {
      if (!down) return;
      const dx = e.clientX - down.x, dy = e.clientY - down.y, dt = performance.now() - down.t;
      down = null;
      this.holding = false;
      if (dy > 90 && Math.abs(dy) > Math.abs(dx)) return this.dialog.close();
      if (dt < 300 && Math.abs(dx) < 12 && Math.abs(dy) < 12) {
        const r = this.stage.getBoundingClientRect();
        (e.clientX - r.left) < r.width / 3 ? this.prev() : this.next();
      }
    };
    this.stage.addEventListener('pointerup', end);
    this.stage.addEventListener('pointercancel', () => { down = null; this.holding = false; });

    this.querySelectorAll('[data-bundle-form]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        if (!(window.JM && window.JM.addToCart)) return; // graceful fallback: normal POST
        e.preventDefault();
        const data = new FormData(form);
        this.dialog.close(); // the cart drawer must not sit under the top-layer dialog
        window.JM.addToCart(data).catch((err) => {
          if (window.JM.toast) window.JM.toast((err && err.message) || 'Could not add to cart');
        });
      });
    });
  }

  initRow() {
    const row = this.querySelector('[data-avatar-row]');
    const prev = this.querySelector('[data-row-prev]');
    const next = this.querySelector('[data-row-next]');
    if (!row || !prev || !next) return;
    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      prev.hidden = max <= 4 || row.scrollLeft <= 4;
      next.hidden = max <= 4 || row.scrollLeft >= max - 4;
    };
    const step = (dir) => row.scrollBy({ left: dir * Math.max(row.clientWidth * 0.8, 200), behavior: this.reduced ? 'auto' : 'smooth' });
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  readViewed() {
    try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (e) { return []; }
  }

  markViewed(i) {
    const id = this.avatars[i] && this.avatars[i].dataset.storyId;
    if (!id) return;
    this.avatars[i].setAttribute('data-seen', '');
    if (this.viewed.includes(id)) return;
    this.viewed.push(id);
    try { localStorage.setItem(this.key, JSON.stringify(this.viewed)); } catch (e) { /* storage unavailable */ }
  }

  open(i) {
    if (!this.dialog.open) this.dialog.showModal();
    this.go(i);
  }

  go(i) {
    if (i >= this.panels.length) return this.dialog.close();
    this.index = Math.max(0, i);
    this.elapsed = 0;
    this.panels.forEach((p, n) => { p.hidden = n !== this.index; });
    this.segs.forEach((s, n) => {
      s.classList.toggle('is-done', n < this.index);
      s.firstElementChild.style.transform = n < this.index ? '' : 'scaleX(0)';
    });
    this.markViewed(this.index);
    cancelAnimationFrame(this.raf);
    this.last = performance.now();
    this.raf = requestAnimationFrame((t) => this.tick(t));
  }

  next() { this.go(this.index + 1); }
  prev() { this.go(this.index - 1); }

  tick(now) {
    if (!this.dialog.open) return;
    const dt = now - this.last;
    this.last = now;
    if (!this.holding && !this.tabHidden && !this.reduced) {
      this.elapsed += dt;
      const seg = this.segs[this.index].firstElementChild;
      seg.style.transform = 'scaleX(' + Math.min(this.elapsed / this.duration, 1) + ')';
      if (this.elapsed >= this.duration) return this.next();
    }
    this.raf = requestAnimationFrame((t) => this.tick(t));
  }

  onClose() {
    cancelAnimationFrame(this.raf);
    this.holding = false;
    if (this.opener) this.opener.focus();
  }
}
if (!customElements.get('bundle-stories')) customElements.define('bundle-stories', BundleStories);
